<template>
    <div class="choose-box">
        <h2 class="choose-box--title">{{$t('BUMPER_BOX_TITLE', { theBox: bumperBoxName })}}</h2>
        <img :src="getImageUrl" alt=""  class="choose-box--image">
        <div class="choose-box--container">
            <p class="choose-box--container--heading">{{$t('BUMPER_BOX_CHOOSE_SIZE')}}</p>
            <div class="choose-box--container--options">
                <div class="choose-box--container--options--lite" @click="boxSelected = 'lite'" v-if="activeProductFamily !== 'lite' && showEssentialBox">
                    <h2 class="choose-box--container--options--lite--heading" :class="{ active: boxSelected === 'lite' }">{{$t('BUMPER_BOX_LITE')}}</h2>
                    <p class="choose-box--container--options--lite--price">{{`£${liteBoxPrice} a box`}}</p>
                    <p class="choose-box--container--options--lite--delivery-not-included">{{$t('BUMPER_BOX_DELIVERY_NOT_INCLUDED')}}</p>
                </div>
                <div class="choose-box--container--options--standard" @click="boxSelected = 'standard'" v-if="activeProductFamily !== 'standard' || !showEssentialBox">
                    <h2 class="choose-box--container--options--standard--heading" :class="{ active: boxSelected === 'standard' }">{{$t('BUMPER_BOX_STANDARD')}}</h2>
                    <p class="choose-box--container--options--standard--price">{{`£${standardBoxPrice} a box`}}</p>
                    <p class="choose-box--container--options--lite--delivery-not-included">{{$t('BUMPER_BOX_DELIVERY_NOT_INCLUDED')}}</p>
                </div>
                <div class="choose-box--container--options--bumper" @click="boxSelected = 'bumper'" v-if="activeProductFamily !== 'bumper'">
                    <h2 class="choose-box--container--options--bumper--heading" :class="{ active: boxSelected === 'bumper' }">{{$t('BUMPER_BOX_BUMPER', { theBox: bumperBoxName })}}</h2>
                    <p class="choose-box--container--options--bumper--price">{{`£${bumperBoxPrice} a box`}}</p>
                    <p class="choose-box--container--options--lite--delivery-not-included">{{$t('BUMPER_BOX_DELIVERY_NOT_INCLUDED')}}</p>
                </div>
            </div>
            <p class="choose-box--container--text">{{boxTypeText}}</p>
            <p class="choose-box--container--info">{{$t('GIFTING_BUMPER_BOX_INFO')}}</p>
            <button class="btn btn--primary" v-if="showCTA" @click="showUpgradeModal = true">{{$t(upgradeCTAText)}}</button>
        </div>
        <transition name="transitionFade">
            <bumper-box-upgrade-modal
                v-if="showUpgradeModal"
                :subscription="activeSubscription"
                :selectedBox="selectedBox"
                @closed="showUpgradeModal = false"/>
        </transition>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { PRODUCT_FAMILY_BUMPER, PRODUCT_FAMILY_STANDARD, PRODUCT_FAMILY_LITE, DISPLAY_NAME_BUMPER } from '@/constants/Gifting';
import BumperBoxUpgradeModal from '@/components/partials/account/bumper-box/BumperBoxUpgradeModal';
import { getBoxPrice } from '@/helpers/QueryHelpers';

export default {
    name: 'BumperBoxUpgrade',
    components: { BumperBoxUpgradeModal },
    data() {
        return {
            boxSelected: 'standard', // Using 'standard' for default selected box.
            showUpgradeModal: false,
            bumperBoxName: DISPLAY_NAME_BUMPER,
        };
    },
    computed: {
        ...mapGetters({
            activeSubscription: 'subscription/getActiveSubscription',
            products: 'subscription/getProductTypes',
            boxPrice: 'register/getBoxPrice',
            deliveryPrice: 'register/getDeliveryPrice',
        }),
        currency() {
            return this.$store.getters['localisation/getCurrency'];
        },
        boxTypeText() {
            if (this.boxSelected === PRODUCT_FAMILY_BUMPER) {
                return this.$t('BUMPER_BOX_BUMPER_TEXT', { theBox: DISPLAY_NAME_BUMPER });
            } else if (this.boxSelected === PRODUCT_FAMILY_STANDARD) {
                return this.$t('BUMPER_BOX_STANDARD_TEXT');
            } else if (this.boxSelected === PRODUCT_FAMILY_LITE && this.showEssentialBox) {
                return this.$t('BUMPER_BOX_LITE_TEXT');
            } else {
                return this.$t('BUMPER_BOX_UPGRADE_TEXT');
            }
        },
        showEssentialBox() {
            return this.activeSubscription?.show_essential_box;
        },
        activeProductFamily() {
            return this.activeSubscription?.product_family;
        },
        liteBoxPrice() {
            return process.env.VUE_APP_LITE_BOX_PRICE;
        },
        bumperBoxPrice() {
            return process.env.VUE_APP_BUMPER_BOX_PRICE;
        },
        standardBoxPrice() {
            return getBoxPrice(this.boxPrice, this.deliveryPrice);
        },
        selectedBox() {
            return this.products.filter(item => item.product_family === this.boxSelected)[0];
        },
        selectedBoxPrice() {
            return this.boxSelected === 'bumper' ? this.bumperBoxPrice : this.standardBoxPrice;
        },
        getImageUrl() {
            if (this.boxSelected === PRODUCT_FAMILY_BUMPER) {
                return require('@/assets/images/products/bumper.jpg');
            } else if (this.boxSelected === PRODUCT_FAMILY_STANDARD) {
                return require('@/assets/images/products/standard.jpg');
            } else if (this.boxSelected === PRODUCT_FAMILY_LITE) {
                return require('@/assets/images/products/lite.jpg');
            }
        },
        upgradeCTAText() {
            return this.activeSubscription?.product_family.toLowerCase() === PRODUCT_FAMILY_BUMPER ? 'BUMPER_BOX_CTA_BUMPER' : 'BUMPER_BOX_CTA_STANDARD';
        },
        selectedBoxName() {
            return this.boxSelected;
        },
        showCTA() {
            return this.activeSubscription?.product_family.toLowerCase() !== this.selectedBoxName;
        },
        selectedBoxId() {
            return this.products.filter(item => item.product_family === this.selectedBoxName)[0].id;
        },
    },
    updated() {
        this.$store.dispatch('account/fetchUserData', true);
    },
};
</script>

<style lang="scss" scoped>
.choose-box {
    margin: 40px auto;
    margin-bottom: 20px;
    max-width: 700px;
    padding-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    &--title {
        text-align: center;
    }
    &--image {
        width: 100%;
        max-width: 500px;
        display: block;
    }
    &--container {
        width: 100%;
        max-width: 500px;
        padding: 10px 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        &--heading {
            text-align: center;
            font-weight: 400;
        }
        &--text {
            text-align: center;
            margin: 20px auto;
        }
        &--info {
            text-align: center;
            margin-bottom: 10px;
            font-size: 15px;
        }
        &--sub-heading {
            text-align: center;
            margin: 20px 10px auto;
        }
        &--options {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-around;
            gap: 20px;
            &--bumper, &--standard, &--lite {
                &--price {
                    text-align: center;
                    font-weight: 600;
                }
                &--heading {
                    text-align: center;
                    font-size: 25px;
                    padding: 5px 20px;
                    opacity: 0.5;
                }
                &--delivery-not-included {
                    margin: 0;
                    text-align: center;
                    font-size: 15px;
                }
                &:hover {
                    cursor: pointer;
                }
            }

        }
        button {
            min-width: 200px;
        }
    }
}
.active {
    opacity: 1;
    border: 1px solid #1a1a1a;
}
</style>
